import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoIcon } from "@/components/Icon";
import { ApiError, apiJson, apiPostJson } from "@/lib/api";
import { getApiBase } from "@/lib/config";
import { useAuthUser, signOut } from "@/lib/auth";

// Account page — profile, password change, Google connect, sign-out.
//
// Lives behind <RequireAuth /> in the router so we never render with a null
// user once `pending` clears. The hook does the cache + revalidate dance.
//
// Password rules mirror the server (validPassword in backend/internal/httpapi/auth_password.go):
// length ≥ 8 AND has digit OR symbol. We surface server errors verbatim
// because the canonical complaint always comes from the server.

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className="border bg-surface mb-[18px]"
      style={{ borderColor: "var(--rule)", borderRadius: 14, padding: "22px 22px 24px" }}
    >
      <h2
        className="m-0 mb-[14px]"
        style={{
          fontFamily: '"Source Serif 4", Georgia, serif',
          fontSize: 17,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--ink)",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

// Row renders one profile/key-value pair with a top-border separator
// (the design's .prow rule). Pass `first` for the first row in a stack
// so it skips the separator. Caller-driven rather than :first-of-type
// because inline styles override stylesheet selectors.
function Row({ label, value, first = false }: { label: string; value: React.ReactNode; first?: boolean }) {
  return (
    <div
      className="flex items-center justify-between gap-3"
      style={{
        padding: first ? "4px 0 13px" : "13px 0",
        borderTop: first ? "none" : "1px solid var(--rule)",
        fontSize: 13,
      }}
    >
      <span style={{ color: "var(--ink-3)", whiteSpace: "nowrap", fontSize: 13 }}>{label}</span>
      <span
        style={{
          fontSize: 13.5,
          color: "var(--ink)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
          // minWidth:0 lets a long value (email, agency name) wrap or
          // ellipsis instead of shoving the row past the viewport edge.
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Banner({ kind, message }: { kind: "error" | "info"; message: string }) {
  if (!message) return null;
  const base =
    "mb-3 rounded-md px-3 py-2 text-[12.5px] border";
  const cls =
    kind === "error"
      ? "border-red-500/40 bg-red-500/10 text-red-500"
      : "border-accent/40 bg-accent-dim text-text-sec";
  return <div className={`${base} ${cls}`}>{message}</div>;
}

function PasswordChangeForm() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setOk(false);
    if (!current || !next) {
      setErr(t("auth.fillBoth"));
      return;
    }
    setBusy(true);
    try {
      await apiPostJson("/auth/password/change", {
        old_password: current,
        new_password: next,
      });
      setOk(true);
      setCurrent("");
      setNext("");
    } catch (e) {
      const ae = e as ApiError;
      if (ae.status === 401) setErr(t("auth.account.currentWrong"));
      else if (ae.status === 400) setErr(t("auth.errorWeakPassword"));
      else setErr(ae.message || t("auth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12.5,
    fontWeight: 500,
    color: "var(--ink-2)",
    marginBottom: 7,
    letterSpacing: "-0.005em",
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--rule-strong)",
    borderRadius: 9,
    background: "#fff",
    fontSize: 13.5,
    color: "var(--ink)",
    outline: "none",
  };

  return (
    <form onSubmit={submit}>
      {err && <Banner kind="error" message={err} />}
      {ok && <Banner kind="info" message={t("auth.account.passwordChanged")} />}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>{t("auth.account.currentPassword")}</label>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>{t("auth.account.newPassword")}</label>
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          style={inputStyle}
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "10px 16px",
          borderRadius: 9,
          fontSize: 13,
          fontWeight: 500,
          cursor: busy ? "default" : "pointer",
          border: "1px solid var(--accent)",
          background: "var(--accent)",
          color: "#f4f1ea",
          letterSpacing: "-0.005em",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "…" : t("auth.account.changePassword")}
      </button>
    </form>
  );
}

// UpgradeToProInline — light upgrade CTA shown on /account for buyers
// who want to flip to agent role. Single PATCH /auth/profile call;
// success refreshes useAuthUser via apiPatchJson + a hard reload (the
// app shell branches on role, so we want a clean re-render).
function UpgradeToProInline() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [cea, setCea] = useState("");
  const [agency, setAgency] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      // The backend's UpdateProfile endpoint accepts role + cea_number +
      // agency in one PATCH (see internal/auth/store.go).
      const res = await fetch(`${getApiBase()}/auth/profile`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role: "pro", cea_number: cea.trim(), agency: agency.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      // Hard reload — useAuthUser caches role in localStorage and the
      // shell layout key is role-dependent. A reload is the simplest
      // way to land on the agent shell with a fresh auth state.
      window.location.href = "/pro/dashboard";
    } catch (e) {
      setErr((e as Error)?.message ?? "Upgrade failed");
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
        <div className="text-[12.5px] text-emerald-900">
          <span className="font-semibold">
            {t("auth.account.upgradeProTitle", { defaultValue: "Are you a property agent?" })}
          </span>
          <span className="ml-1 text-emerald-700">
            {t("auth.account.upgradeProBody", {
              defaultValue: "Switch to the agent workspace to manage listings, viewings and leads.",
            })}
          </span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="rounded-md bg-emerald-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-emerald-700"
        >
          {t("auth.account.upgradeProCta", { defaultValue: "Upgrade to Pro" })}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3">
      <div className="text-[12.5px] font-semibold text-emerald-900">
        {t("auth.account.upgradeFormTitle", { defaultValue: "Activate Pro account" })}
      </div>
      <p className="text-[11.5px] text-emerald-800">
        {t("auth.account.upgradeFormBody", {
          defaultValue: "Optional: CEA license number and agency name. You can fill these later.",
        })}
      </p>
      <input
        value={cea}
        onChange={(e) => setCea(e.target.value)}
        placeholder={t("auth.account.upgradeFormCea", { defaultValue: "CEA reg # (optional)" })}
        className="w-full rounded-md border border-emerald-300 bg-white px-2 py-1 text-[13px]"
      />
      <input
        value={agency}
        onChange={(e) => setAgency(e.target.value)}
        placeholder={t("auth.account.upgradeFormAgency", { defaultValue: "Agency name (optional)" })}
        className="w-full rounded-md border border-emerald-300 bg-white px-2 py-1 text-[13px]"
      />
      {err && <div className="text-[11.5px] text-rose-700">{err}</div>}
      <div className="flex gap-2">
        <button
          onClick={() => setOpen(false)}
          disabled={busy}
          className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-[12px] font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
        >
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="ml-auto rounded-md bg-emerald-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy
            ? t("common.saving", { defaultValue: "Activating…" })
            : t("auth.account.upgradeFormConfirm", { defaultValue: "Activate Pro" })}
        </button>
      </div>
    </div>
  );
}

// MyAgentsSection — buyer can link their account to an agent by email.
// Once the link is 'active', the agent's inbox auto-surfaces this
// buyer's LLM conversations (no manual thread creation needed by
// either side).
//
// Status states:
//   invited  — agent hasn't accepted yet
//   active   — accepted; conversations are visible to the agent
//   revoked  — either side cut the link
//
// We list all non-revoked links plus an inline form to add a new one.
function MyAgentsSection() {
  const { t } = useTranslation();
  type AgentLink = {
    id: string;
    agent_id: string;
    status: "invited" | "active" | "revoked";
    invited_by: "buyer" | "agent";
    agent_name?: string;
    agent_email?: string;
    created_at: string;
  };
  const [agents, setAgents] = useState<AgentLink[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refetch = () => {
    fetch(`${getApiBase()}/auth/agents`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        setAgents((j.agents as AgentLink[]) ?? []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };
  useEffect(() => {
    refetch();
  }, []);

  const invite = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(`${getApiBase()}/auth/agents`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      setEmail("");
      refetch();
    } catch (e) {
      setErr((e as Error)?.message ?? "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm(t("auth.account.agentRevokeConfirm", { defaultValue: "Revoke this agent's access to your conversations?" }))) return;
    await fetch(`${getApiBase()}/auth/agents/${id}`, { method: "DELETE", credentials: "include" });
    refetch();
  };

  const visible = agents.filter((a) => a.status !== "revoked");

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-text-muted">
        {t("auth.account.myAgentsBody", {
          defaultValue: "Link an agent by their email so they can see your chat history and proactively help with viewings. You can revoke at any time.",
        })}
      </p>

      {!loaded ? (
        <div className="text-[12.5px] text-text-muted">{t("common.loading", { defaultValue: "Loading…" })}</div>
      ) : visible.length === 0 ? (
        <div className="text-[12.5px] text-text-muted">
          {t("auth.account.agentsEmpty", { defaultValue: "No linked agents yet." })}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {visible.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2">
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-text">{a.agent_name || a.agent_email}</div>
                <div className="text-[11px] text-text-muted">
                  {a.agent_email}
                  <span className="ml-2 inline-flex rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-sec">
                    {a.status === "invited"
                      ? t("auth.account.agentStatusInvited", { defaultValue: "pending" })
                      : t("auth.account.agentStatusActive", { defaultValue: "active" })}
                  </span>
                </div>
              </div>
              <button
                onClick={() => void revoke(a.id)}
                className="shrink-0 rounded-md border border-rose-200 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50"
              >
                {t("auth.account.agentRevoke", { defaultValue: "Revoke" })}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5">
        <p className="mb-1.5 text-[11.5px] font-semibold text-emerald-900">
          {t("auth.account.agentInviteTitle", { defaultValue: "Add an agent by email" })}
        </p>
        <div className="flex gap-2">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="agent@agency.com.sg"
            className="flex-1 rounded-md border border-emerald-300 bg-white px-2 py-1 text-[13px]"
          />
          <button
            onClick={() => void invite()}
            disabled={busy || !email.trim()}
            className="rounded-md bg-emerald-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy
              ? t("common.saving", { defaultValue: "Sending…" })
              : t("auth.account.agentInviteCta", { defaultValue: "Send invite" })}
          </button>
        </div>
        {err && <div className="mt-1.5 text-[11.5px] text-rose-700">{err}</div>}
      </div>
    </div>
  );
}

// PracticeStatsSection — rolling-12-month rollup of CEA-attributable
// transactions for the signed-in agent. Backed by /agent/practice-stats,
// which proxies to the data engine's aggregation over cea_transactions
// (data.gov.sg d_ee7e46d3c57f7865790704632b0aef71).
//
// The KPI grid intentionally substitutes "Active months" for the design
// mock's "Gross transaction value" — the CEA dataset has no price column,
// so we surface a metric we can compute truthfully instead of fabricating
// dollars. Per CLAUDE.md: engineering owns data, the LLM owns conversation.
//
// States:
//   - user.cea_number missing → quiet inline notice, no fetch
//   - 200 with empty data    → empty-state copy inside the card
//   - 200 with data          → KPI grid + bar chart + town list + donuts
type PracticeStats = {
  registration_no: string;
  mode: "sale" | "rent";
  window_from: string;
  window_to: string;
  deals: number;
  prior_deals: number;
  active_months: number;
  licensed_since?: string | null;
  monthly: { month: string; count: number }[];
  towns: { name: string; count: number }[];
  representation: { name: string; count: number }[];
  tx_types: { name: string; count: number }[];
};

// titleCaseSG — uppercase-source displays from URA/CEA convert at the
// presentation boundary per the project's URA-titlecase rule. Handles
// space- and slash-separated tokens ("ANG MO KIO", "KALLANG/WHAMPOA").
function titleCaseSG(s: string): string {
  if (!s) return s;
  return s
    .toLowerCase()
    .split(/(\s+|\/)/)
    .map((part) =>
      /^\s+$/.test(part) || part === "/"
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
}

function formatTenure(since: Date, now = new Date()): { short: string; sinceLabel: string } {
  let y = now.getFullYear() - since.getFullYear();
  let m = now.getMonth() - since.getMonth();
  let d = now.getDate() - since.getDate();
  if (d < 0) {
    m -= 1;
    const prev = new Date(now.getFullYear(), now.getMonth(), 0);
    d += prev.getDate();
  }
  if (m < 0) {
    y -= 1;
    m += 12;
  }
  return {
    short: `${y}y ${m}m ${d}d`,
    sinceLabel: since.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
  };
}

// Series colours — keyed off `mode` so the whole card stays inside one
// hue (green for sale, gold for rental). Secondary/tertiary shades hold
// up against the surface tokens in both light and dark theme.
function modeColors(mode: "sale" | "rent") {
  return mode === "sale"
    ? { primary: "var(--accent)", secondary: "#6b8c7e", tertiary: "#b8c4be" }
    : { primary: "var(--ai-accent)", secondary: "#b89a5a", tertiary: "#d9c896" };
}

function Donut({ data, colors }: { data: { name: string; count: number }[]; colors: string[] }) {
  const total = data.reduce((s, x) => s + x.count, 0);
  const r = 52;
  const c = 64;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const segs = total === 0 ? [] : data.map((s, i) => {
    const frac = s.count / total;
    const len = circ * frac;
    const el = (
      <circle
        key={i}
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke={colors[i] ?? "var(--ink-4)"}
        strokeWidth={16}
        strokeDasharray={`${len} ${circ - len}`}
        strokeDashoffset={-offset}
      />
    );
    offset += len;
    return el;
  });
  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div className="relative" style={{ width: 132, height: 132 }}>
        <svg width={132} height={132} viewBox="0 0 128 128" style={{ transform: "rotate(-90deg)" }}>
          <circle cx={c} cy={c} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={16} />
          {segs}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontSize: 26,
              fontWeight: 500,
              color: "var(--ink)",
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {total}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "var(--ink-3)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginTop: 4,
            }}
          >
            Total
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 w-full">
        {data.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-[12px]">
            <span
              className="shrink-0 rounded-sm"
              style={{ width: 9, height: 9, background: colors[i] ?? "var(--ink-4)" }}
            />
            <span className="flex-1" style={{ color: "var(--ink-2)" }}>{titleCaseSG(s.name)}</span>
            <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, color: "var(--ink-3)" }}>
              {s.count} · {total === 0 ? 0 : Math.round((s.count / total) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyBars({ data, color }: { data: { month: string; count: number }[]; color: string }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 480;
  const h = 110;
  const pad = 4;
  const gap = 6;
  const bw = (w - pad * 2 - gap * (data.length - 1)) / data.length;
  const labels = data.map((d) => {
    const m = new Date(d.month);
    return m.toLocaleString("en-US", { month: "short" });
  });
  return (
    <div
      className="mb-3.5"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--rule)",
        borderRadius: 10,
        padding: "14px 14px 10px",
      }}
    >
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "auto", display: "block" }}>
        <line x1={pad} y1={h - 0.5} x2={w - pad} y2={h - 0.5} stroke="var(--rule)" strokeWidth={1} />
        {data.map((d, i) => {
          const bh = Math.max(2, (d.count / max) * (h - 14));
          const x = pad + i * (bw + gap);
          const y = h - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={bw} height={bh} rx={2} fill={color} opacity={0.92} />
              <text
                x={x + bw / 2}
                y={y - 3}
                textAnchor="middle"
                fontFamily="ui-monospace, monospace"
                fontSize={12}
                fill="var(--ink-3)"
              >
                {d.count}
              </text>
            </g>
          );
        })}
      </svg>
      <div
        className="flex justify-between"
        style={{
          padding: "4px 2px 0",
          fontSize: 10,
          color: "var(--ink-4)",
          fontFamily: "ui-monospace, monospace",
          letterSpacing: "0.04em",
        }}
      >
        {labels.map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>
    </div>
  );
}

function TownList({ towns, mode }: { towns: { name: string; count: number }[]; mode: "sale" | "rent" }) {
  const max = Math.max(1, ...towns.map((t) => t.count));
  const fill = mode === "sale" ? "var(--accent)" : "var(--ai-accent)";
  return (
    <div className="flex flex-col gap-2.5">
      {towns.map((t, i) => (
        <div key={i} className="grid items-center gap-x-2" style={{ gridTemplateColumns: "1fr auto" }}>
          <span className="text-[12.5px]" style={{ color: "var(--ink)" }}>
            {titleCaseSG(t.name)}
          </span>
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, color: "var(--ink-3)" }}>
            {t.count}
          </span>
          <span
            className="col-span-2"
            style={{ height: 5, background: "var(--surface-2)", borderRadius: 3, overflow: "hidden" }}
          >
            <i
              style={{
                display: "block",
                height: "100%",
                background: fill,
                borderRadius: 3,
                width: `${(t.count / max) * 100}%`,
                transition: "width 600ms cubic-bezier(.2,.7,.2,1)",
              }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

function PracticeStatsSection({ ceaNumber }: { ceaNumber: string }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"sale" | "rent">("sale");
  const [stats, setStats] = useState<PracticeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    if (!ceaNumber) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setEmpty(false);
    apiJson<PracticeStats>(`/agent/practice-stats?mode=${mode}`)
      .then((d) => {
        if (cancelled) return;
        setStats(d);
        setEmpty(d.deals === 0 && d.prior_deals === 0);
      })
      .catch((e: ApiError) => {
        if (cancelled) return;
        if (e.status === 404) {
          setStats(null);
          setEmpty(true);
        } else {
          setErr(e.message || t("auth.account.practice.error"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, ceaNumber, t]);

  const colors = useMemo(() => modeColors(mode), [mode]);

  if (!ceaNumber) {
    return <p className="text-[12.5px]" style={{ color: "var(--ink-3)" }}>{t("auth.account.practice.missingCea")}</p>;
  }

  const tenure = stats?.licensed_since
    ? formatTenure(new Date(stats.licensed_since))
    : null;

  const deltaPct = stats && stats.prior_deals > 0
    ? Math.round(((stats.deals - stats.prior_deals) / stats.prior_deals) * 100)
    : null;
  const deltaLabel =
    deltaPct === null
      ? null
      : deltaPct > 0
      ? t("auth.account.practice.kpiDeltaUp", { pct: deltaPct })
      : deltaPct < 0
      ? t("auth.account.practice.kpiDeltaDown", { pct: deltaPct })
      : t("auth.account.practice.kpiDeltaSame");

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <div
            className="text-[11.5px] mt-1.5"
            style={{ color: "var(--ink-3)", letterSpacing: "0.04em", textTransform: "uppercase" }}
          >
            {t("auth.account.practice.sub")}
          </div>
        </div>
        <div
          className="inline-flex gap-0.5 p-[3px] rounded-md"
          style={{ background: "var(--surface-2)" }}
        >
          {(["sale", "rent"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className="rounded-md px-3 py-1.5 text-[12px] font-medium transition"
              style={{
                background: m === mode ? "var(--surface)" : "transparent",
                color: m === mode ? "var(--ink)" : "var(--ink-3)",
                boxShadow: m === mode ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              }}
            >
              <span
                className="inline-block rounded-full mr-1.5 align-middle"
                style={{
                  width: 7,
                  height: 7,
                  background: m === "sale" ? "var(--accent)" : "var(--ai-accent)",
                }}
              />
              {m === "sale" ? t("auth.account.practice.modeSale") : t("auth.account.practice.modeRent")}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-[12.5px]" style={{ color: "var(--ink-3)" }}>{t("auth.account.practice.loading")}</div>
      ) : err ? (
        <Banner kind="error" message={err} />
      ) : empty || !stats ? (
        <div className="text-[12.5px]" style={{ color: "var(--ink-3)" }}>{t("auth.account.practice.empty")}</div>
      ) : (
        <>
          {/* KPI grid */}
          <div
            className="grid mb-4"
            style={{
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
              background: "var(--rule)",
              border: "1px solid var(--rule)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <div style={{ background: "var(--surface)", padding: "12px 14px" }}>
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--ink-3)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {t("auth.account.practice.kpiDeals")}
              </div>
              <div
                style={{
                  fontFamily: '"Source Serif 4", Georgia, serif',
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {stats.deals}
              </div>
              {deltaLabel && (
                <div
                  style={{
                    fontSize: 11,
                    color: deltaPct !== null && deltaPct >= 0 ? "var(--pos)" : "var(--neg)",
                    marginTop: 2,
                  }}
                >
                  {deltaLabel}
                </div>
              )}
            </div>
            <div style={{ background: "var(--surface)", padding: "12px 14px" }}>
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--ink-3)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {t("auth.account.practice.kpiActiveMonths")}
              </div>
              <div
                style={{
                  fontFamily: '"Source Serif 4", Georgia, serif',
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {stats.active_months}
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                {t("auth.account.practice.kpiActiveMonthsSub")}
              </div>
            </div>
            <div style={{ background: "var(--surface)", padding: "12px 14px" }}>
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--ink-3)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {t("auth.account.practice.kpiLicensed")}
              </div>
              <div
                style={{
                  fontFamily: '"Source Serif 4", Georgia, serif',
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {tenure ? tenure.short : "—"}
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                {tenure
                  ? t("auth.account.practice.kpiLicensedSince", { date: tenure.sinceLabel })
                  : t("auth.account.practice.kpiLicensedMissing")}
              </div>
            </div>
          </div>

          {/* Monthly bars */}
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 10,
            }}
          >
            {t("auth.account.practice.monthly")}
          </div>
          <MonthlyBars data={stats.monthly} color={colors.primary} />

          {/* Top towns */}
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--rule)",
              borderRadius: 10,
              padding: 14,
              marginTop: 6,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "var(--ink-3)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              {t("auth.account.practice.topTowns")}
            </div>
            <TownList towns={stats.towns} mode={mode} />
          </div>

          {/* Donuts */}
          <div className="grid mt-3.5 gap-3.5" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--rule)",
                borderRadius: 10,
                padding: 14,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                {t("auth.account.practice.representation")}
              </div>
              <Donut
                data={stats.representation}
                colors={[colors.primary, colors.secondary, colors.tertiary]}
              />
            </div>
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--rule)",
                borderRadius: 10,
                padding: 14,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                {t("auth.account.practice.txType")}
              </div>
              <Donut
                data={stats.tx_types}
                colors={[colors.primary, colors.secondary, colors.tertiary]}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// TagInput — chip-style multi-value editor. Replaces the older
// comma-separated text inputs called out as awkward in the design chat.
// Commits on Enter / comma / Tab / blur; Backspace at empty input pops
// the last chip; pasted text with commas or newlines splits across chips.
function TagInput({
  values,
  onChange,
  placeholder,
  mono = false,
  candidates,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  mono?: boolean;
  // When provided, surfaces an "available options" row below the input.
  // Clicking a candidate chip adds it to values; removing a selected
  // chip puts it back in the suggestion row. Free-typing still works
  // for entries not in the list, so this is suggestions, not gating.
  candidates?: readonly string[];
}) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const commit = (raw: string) => {
    const v = raw.trim().replace(/,$/, "").trim();
    if (!v) return;
    if (values.includes(v)) return;
    onChange([...values, v]);
  };
  const monoFamily = mono ? "ui-monospace, monospace" : undefined;
  const unselectedCandidates = (candidates ?? []).filter((c) => !values.includes(c));
  return (
    <div>
    <div
      onClick={(e) => {
        // Clicking blank space inside the wrapper focuses the input.
        const target = e.target as HTMLElement;
        if (target.tagName === "DIV") {
          target.querySelector("input")?.focus();
        }
      }}
      className="flex flex-wrap"
      style={{
        gap: 6,
        padding: "7px 8px",
        border: `1px solid ${focused ? "var(--accent)" : "var(--rule-strong)"}`,
        borderRadius: 9,
        background: "#fff",
        minHeight: 42,
        cursor: "text",
        boxShadow: focused ? "0 0 0 3px var(--accent-soft)" : undefined,
        transition: "border-color 120ms, box-shadow 120ms",
      }}
    >
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex items-center"
          style={{
            gap: 4,
            padding: mono ? "3px 4px 3px 9px" : "3px 4px 3px 10px",
            background: "var(--accent-soft)",
            color: "var(--accent-ink)",
            borderRadius: 6,
            fontSize: mono ? 12 : 12.5,
            lineHeight: 1.4,
            fontFamily: monoFamily,
          }}
        >
          <span>{v}</span>
          <button
            type="button"
            onClick={() => onChange(values.filter((_, idx) => idx !== i))}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--accent)",
              padding: 2,
              borderRadius: 4,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Remove"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </span>
      ))}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (text.trim()) {
            commit(text);
            setText("");
          }
        }}
        onPaste={(e) => {
          const data = e.clipboardData.getData("text");
          if (/[,\n]/.test(data)) {
            e.preventDefault();
            const next = [...values];
            data
              .split(/[,\n]/)
              .map((s) => s.trim())
              .filter(Boolean)
              .forEach((v) => {
                if (!next.includes(v)) next.push(v);
              });
            onChange(next);
            setText("");
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
            if (text.trim()) {
              e.preventDefault();
              commit(text);
              setText("");
            }
          } else if (e.key === "Backspace" && text === "" && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 80,
          border: "none",
          outline: "none",
          padding: "4px 6px",
          background: "transparent",
          fontSize: 13.5,
          color: "var(--ink)",
          fontFamily: monoFamily,
        }}
      />
    </div>
    {unselectedCandidates.length > 0 && (
      <div
        className="flex flex-wrap"
        style={{ gap: 6, marginTop: 8 }}
      >
        {unselectedCandidates.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange([...values, c])}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: mono ? "3px 8px 3px 7px" : "3px 10px 3px 8px",
              background: "transparent",
              color: "var(--ink-3)",
              border: "1px dashed var(--rule-strong)",
              borderRadius: 6,
              fontSize: mono ? 12 : 12.5,
              lineHeight: 1.4,
              cursor: "pointer",
              fontFamily: monoFamily,
              transition: "all 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--accent-soft)";
              e.currentTarget.style.color = "var(--accent-ink)";
              e.currentTarget.style.borderColor = "var(--accent)";
              e.currentTarget.style.borderStyle = "solid";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--ink-3)";
              e.currentTarget.style.borderColor = "var(--rule-strong)";
              e.currentTarget.style.borderStyle = "dashed";
            }}
          >
            <span style={{ fontSize: mono ? 11 : 12, lineHeight: 1 }}>+</span>
            <span>{c}</span>
          </button>
        ))}
      </div>
    )}
    </div>
  );
}

// URA postal districts — Singapore has 28 codes ("01" through "28").
// We surface the full list as candidate chips below the Specialty
// districts tag-input; agents can still type a code manually.
const URA_DISTRICT_CANDIDATES: readonly string[] = Array.from(
  { length: 28 },
  (_, i) => String(i + 1).padStart(2, "0")
);

// Languages commonly spoken in Singapore residential property
// conversations. Order roughly by national prevalence; lesser-used
// dialects/regional languages can still be added via free typing.
const LANGUAGE_CANDIDATES: readonly string[] = [
  "English",
  "Mandarin",
  "Malay",
  "Tamil",
  "Hokkien",
  "Cantonese",
  "Teochew",
  "Bahasa Indonesia",
];

// ProAgentProfileSection — pro-only specialty editor.
//
// Shape stays simple: comma-separated text inputs for districts /
// languages, three checkbox chips for property types, a textarea for
// bio, and a URL field for photo. The arrays go to the backend as
// real []string (PG TEXT[]) — see proagent.UpsertProfile.
//
// Pointer-aware partial updates: we always send all five fields so
// the user clearing the bio actually clears it (sending undefined
// would mean "leave alone"). Saved state is round-tripped through
// the GET endpoint to ensure server normalisation lands in the UI.
function ProAgentProfileSection() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string>("");
  const [savedAt, setSavedAt] = useState<number>(0);

  const [districts, setDistricts] = useState<string[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [bio, setBio] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBase()}/agent/profile`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((p: {
        specialty_districts?: string[];
        specialty_property_types?: string[];
        languages?: string[];
        bio?: string;
        photo_url?: string;
      }) => {
        if (cancelled) return;
        setDistricts(p.specialty_districts ?? []);
        setPropertyTypes(p.specialty_property_types ?? []);
        setLanguages(p.languages ?? []);
        setBio(p.bio ?? "");
        setPhotoUrl(p.photo_url ?? "");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  function togglePropertyType(v: string) {
    setPropertyTypes((arr) =>
      arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]
    );
  }

  async function save() {
    setSaving(true);
    setErr("");
    try {
      const res = await fetch(`${getApiBase()}/agent/profile`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          specialty_districts: districts,
          specialty_property_types: propertyTypes,
          languages,
          bio,
          photo_url: photoUrl,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const p = (await res.json()) as {
        specialty_districts?: string[];
        specialty_property_types?: string[];
        languages?: string[];
        bio?: string;
        photo_url?: string;
      };
      setDistricts(p.specialty_districts ?? []);
      setPropertyTypes(p.specialty_property_types ?? []);
      setLanguages(p.languages ?? []);
      setBio(p.bio ?? "");
      setPhotoUrl(p.photo_url ?? "");
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="text-[12.5px]" style={{ color: "var(--ink-3)" }}>
        {t("auth.account.proProfile.loading", { defaultValue: "Loading…" })}
      </div>
    );
  }

  // 5 property types per the design (HDB, Condo, Landed, Commercial,
  // Industrial). The backend stores these as free-form text[]; the lower-
  // case wire values stay backwards-compatible with existing rows.
  const PROPERTY_OPTIONS = [
    { value: "hdb",         label: t("auth.account.proProfile.typeHDB",         { defaultValue: "HDB" }) },
    { value: "condo",       label: t("auth.account.proProfile.typeCondo",       { defaultValue: "Condo" }) },
    { value: "landed",      label: t("auth.account.proProfile.typeLanded",      { defaultValue: "Landed" }) },
    { value: "commercial",  label: t("auth.account.proProfile.typeCommercial",  { defaultValue: "Commercial" }) },
    { value: "industrial",  label: t("auth.account.proProfile.typeIndustrial",  { defaultValue: "Industrial" }) },
  ];

  const fieldLabelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12.5,
    fontWeight: 500,
    color: "var(--ink-2)",
    marginBottom: 7,
    letterSpacing: "-0.005em",
  };
  const hintStyle: React.CSSProperties = {
    fontSize: 11.5,
    color: "var(--ink-3)",
    marginTop: 6,
    lineHeight: 1.45,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--rule-strong)",
    borderRadius: 9,
    background: "#fff",
    fontSize: 13.5,
    color: "var(--ink)",
    outline: "none",
  };

  return (
    <div>
      <p
        style={{
          fontSize: 12.5,
          lineHeight: 1.55,
          color: "var(--ink-3)",
          margin: "0 0 18px",
        }}
      >
        {t("auth.account.proProfile.intro", {
          defaultValue:
            "These signals help ProProp route incoming buyer leads to you and surface your card when buyers ask the AI to recommend an agent.",
        })}
      </p>

      <Banner kind="error" message={err} />
      {savedAt > 0 && !err && (
        <Banner
          kind="info"
          message={t("auth.account.proProfile.saved", { defaultValue: "Profile updated." })}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>
          {t("auth.account.proProfile.districts", { defaultValue: "Specialty districts (URA codes)" })}
        </label>
        <TagInput
          values={districts}
          onChange={setDistricts}
          placeholder={t("auth.account.proProfile.districtsTagPlaceholder", { defaultValue: "Add code…" }) as string}
          mono
          candidates={URA_DISTRICT_CANDIDATES}
        />
        <div style={hintStyle}>
          {t("auth.account.proProfile.districtsTagHint", {
            defaultValue:
              'Two-digit URA district codes you cover. Press Enter or comma to add. Strip any "D" prefix.',
          })}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>
          {t("auth.account.proProfile.propertyTypes", { defaultValue: "Property types you handle" })}
        </label>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {PROPERTY_OPTIONS.map((opt) => {
            const active = propertyTypes.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => togglePropertyType(opt.value)}
                style={{
                  padding: "7px 14px",
                  borderRadius: 999,
                  border: `1px solid ${active ? "var(--accent)" : "var(--rule-strong)"}`,
                  background: active ? "var(--accent)" : "var(--surface)",
                  fontSize: 12.5,
                  color: active ? "#f4f1ea" : "var(--ink-2)",
                  cursor: "pointer",
                  transition: "all 120ms",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>
          {t("auth.account.proProfile.languages", { defaultValue: "Languages" })}
        </label>
        <TagInput
          values={languages}
          onChange={setLanguages}
          placeholder={t("auth.account.proProfile.languagesTagPlaceholder", { defaultValue: "Add a language…" }) as string}
          candidates={LANGUAGE_CANDIDATES}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle} htmlFor="bio">
          {t("auth.account.proProfile.bio", { defaultValue: "Bio" })}
        </label>
        <textarea
          id="bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={4}
          maxLength={4000}
          placeholder={t("auth.account.proProfile.bioPlaceholder", {
            defaultValue: "A few sentences about your experience and approach.",
          }) as string}
          style={{
            ...inputStyle,
            resize: "vertical",
            minHeight: 96,
            fontFamily: "inherit",
            lineHeight: 1.5,
          }}
        />
        <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 5, fontVariantNumeric: "tabular-nums" }}>
          {bio.length} / 4000
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle} htmlFor="photo">
          {t("auth.account.proProfile.photo", { defaultValue: "Photo URL" })}
        </label>
        <input
          id="photo"
          value={photoUrl}
          onChange={(e) => setPhotoUrl(e.target.value)}
          placeholder="https://…"
          style={inputStyle}
        />
        <div style={hintStyle}>
          {t("auth.account.proProfile.photoHint", { defaultValue: "Public image URL. Leave blank to use initials avatar." })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "10px 16px",
          borderRadius: 9,
          fontSize: 13,
          fontWeight: 500,
          cursor: saving ? "default" : "pointer",
          border: "1px solid var(--accent)",
          letterSpacing: "-0.005em",
          background: "var(--accent)",
          color: "#f4f1ea",
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving
          ? t("auth.account.proProfile.saving", { defaultValue: "Saving…" })
          : t("auth.account.proProfile.save", { defaultValue: "Save profile" })}
      </button>
    </div>
  );
}

// InviteBuyersSection — sharable link of the form
//   ${origin}/link/agent/${agentId}
// Buyer clicks → either /auth (new user) or auto-link (signed in).
// We don't generate per-recipient tokens because the link maps to a
// public agent_id; the agent_user_links UNIQUE constraint already
// handles re-clicks idempotently.
function InviteBuyersSection({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/link/agent/${agentId}`
    : `/link/agent/${agentId}`;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can be blocked (insecure context, permissions).
      // Fall back to selecting the input so user can copy manually.
    }
  }

  return (
    <div>
      <p
        style={{
          fontSize: 12.5,
          lineHeight: 1.55,
          color: "var(--ink-3)",
          margin: "0 0 18px",
        }}
      >
        {t("auth.account.invite.intro", {
          defaultValue:
            "Share this link with a real buyer (WhatsApp, business card QR). When they click, they'll be linked to you — every ProProp conversation they have shows up in your inbox.",
        })}
      </p>
      <div className="flex items-stretch" style={{ gap: 8 }}>
        <input
          value={url}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "10px 12px",
            border: "1px solid var(--rule-strong)",
            borderRadius: 9,
            background: "#fff",
            fontSize: 12.5,
            color: "var(--ink)",
            fontFamily: "ui-monospace, monospace",
            letterSpacing: "-0.01em",
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={() => void copy()}
          style={{
            padding: "0 16px",
            borderRadius: 9,
            border: `1px solid ${copied ? "var(--accent)" : "var(--rule-strong)"}`,
            background: copied ? "var(--accent)" : "var(--surface)",
            color: copied ? "#f4f1ea" : "var(--ink-2)",
            fontSize: 12.5,
            fontWeight: 500,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 120ms",
          }}
        >
          {copied
            ? t("auth.account.invite.copied", { defaultValue: "Copied ✓" })
            : t("auth.account.invite.copy", { defaultValue: "Copy" })}
        </button>
      </div>
    </div>
  );
}

// GoogleLogo — Google's 4-color G mark. Embedded inline so the button
// renders identically to the design without an asset pipeline dependency.
function GoogleLogo({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.56 2.7-3.87 2.7-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.58-5.05-3.71H.92v2.33A8.99 8.99 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.71A5.41 5.41 0 0 1 3.66 9c0-.59.1-1.17.29-1.71V4.96H.92A9 9 0 0 0 0 9c0 1.45.35 2.83.92 4.04l3.03-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A8.96 8.96 0 0 0 9 0 8.99 8.99 0 0 0 .92 4.96l3.03 2.33C4.66 5.16 6.65 3.58 9 3.58z"/>
    </svg>
  );
}

function GoogleConnectSection({
  hasGoogle,
  accountEmail,
  googleEmail,
}: {
  hasGoogle: boolean;
  accountEmail: string;
  googleEmail?: string;
}) {
  const { t } = useTranslation();
  // /auth/google/link drops a short-lived HttpOnly cookie carrying the
  // signed-in user_id. The callback reads it and writes google_id +
  // google_profile onto THAT user row (skipping email-match), so linking
  // a Google account whose email differs from the ProProp email is
  // safe — no silent account switch or creation.
  const base = getApiBase();
  // PROP-206: backend authGoogleStart reads `next` (an app-relative path,
  // validated by isSafeNextPath), not a `return=` full URL — the old param
  // was ignored so linking never returned to /account.
  const href = `${base}/auth/google/link?next=${encodeURIComponent("/account")}`;
  // Display preference: the Google account's actual email when we have
  // it (post-migration users), falling back to the account email for
  // older rows that pre-date the google_profile JSONB column.
  const displayEmail = googleEmail || accountEmail;
  const mismatch = !!googleEmail && googleEmail.toLowerCase() !== accountEmail.toLowerCase();
  if (hasGoogle) {
    return (
      <div>
        <div
          className="flex items-start"
          style={{
            gap: 12,
            padding: "11px 14px",
            border: "1px solid rgba(29,74,58,0.18)",
            background: "var(--pos-bg)",
            borderRadius: 10,
            marginBottom: 14,
          }}
        >
          <GoogleLogo size={18} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pos)", lineHeight: 1.4 }}>
              {t("auth.account.googleConnectedTitle")}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.45, marginTop: 4 }}>
              {t("auth.account.googleConnectedBody", { email: displayEmail })}
            </div>
            {mismatch && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--warn)",
                  lineHeight: 1.45,
                  marginTop: 6,
                }}
              >
                {t("auth.account.googleEmailMismatch", { accountEmail })}
              </div>
            )}
          </div>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: 999,
              background: "var(--pos)",
              color: "#f4f1ea",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
        <a
          href={href}
          className="inline-flex items-center"
          style={{
            gap: 8,
            padding: "8px 14px 8px 12px",
            borderRadius: 9,
            border: "1px solid var(--rule-strong)",
            background: "var(--surface)",
            fontSize: 12.5,
            fontWeight: 500,
            color: "var(--ink-2)",
            textDecoration: "none",
          }}
        >
          <GoogleLogo size={14} />
          {t("auth.account.googleRelink")}
        </a>
      </div>
    );
  }
  return (
    <div>
      <p
        style={{
          fontSize: 12.5,
          lineHeight: 1.55,
          color: "var(--ink-3)",
          margin: "0 0 14px",
        }}
      >
        {t("auth.account.googleConnectIntro")}
      </p>
      <a
        href={href}
        className="inline-flex items-center"
        style={{
          gap: 10,
          padding: "9px 16px 9px 13px",
          borderRadius: 9,
          border: "1px solid var(--rule-strong)",
          background: "#fff",
          fontSize: 13,
          fontWeight: 500,
          color: "var(--ink)",
          textDecoration: "none",
        }}
      >
        <GoogleLogo />
        {t("auth.account.googleConnect")}
      </a>
    </div>
  );
}

export function Account() {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const navigate = useNavigate();
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const [verifyErr, setVerifyErr] = useState("");

  // RequireAuth has already redirected if user is null + not pending.
  if (!user) return null;

  // The auth_users table carries a real google_id; the backend exposes
  // `google_linked: bool` (derived from google_id IS NOT NULL) on /auth/me
  // so we can show a distinct connected state. Fallback to email_verified
  // is for legacy clients whose cached user record predates the field.
  const hasGoogle = user.google_linked ?? !!user.email_verified;

  async function handleSignOut() {
    await signOut();
    navigate("/auth", { replace: true });
  }

  async function handleResendVerify() {
    setVerifyBusy(true);
    setVerifyErr("");
    try {
      await apiPostJson("/auth/verify/send", {});
      setVerifySent(true);
    } catch (e) {
      const ae = e as ApiError;
      if (ae.status === 429) {
        setVerifyErr(t("auth.errorRateLimit"));
      } else {
        setVerifyErr(ae.message || t("auth.errorGeneric"));
      }
    } finally {
      setVerifyBusy(false);
    }
  }

  return (
    <div className="mx-auto" style={{ maxWidth: 560, padding: "22px 22px 80px" }}>
      {/* Header */}
      <div className="flex items-center justify-between" style={{ padding: "4px 2px 22px" }}>
        <Link to="/" className="flex items-center" style={{ gap: 9, textDecoration: "none" }}>
          <LogoIcon size={22} />
          <span
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
            }}
          >
            ProProp<span style={{ color: "var(--accent)" }}>.</span>
          </span>
        </Link>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center"
          style={{
            gap: 6,
            padding: "7px 12px",
            border: "1px solid var(--rule)",
            borderRadius: 8,
            background: "var(--surface)",
            fontSize: 12.5,
            color: "var(--ink-2)",
          }}
        >
          ← {t("common.back")}
        </button>
      </div>

      <h1
        className="m-0"
        style={{
          fontFamily: '"Source Serif 4", Georgia, serif',
          fontSize: 36,
          fontWeight: 500,
          letterSpacing: "-0.02em",
          color: "var(--ink)",
          marginBottom: 22,
        }}
      >
        {t("auth.account.title")}
      </h1>

      <Section title={t("auth.account.profile")}>
        <Row first label={t("auth.account.name")} value={user.name || "—"} />
        <Row
          label={t("auth.account.emailLabel")}
          value={
            <span className="inline-flex items-center" style={{ gap: 8 }}>
              <span>{user.email}</span>
              {user.email_verified ? (
                <span
                  className="inline-flex items-center"
                  style={{
                    gap: 4,
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    padding: "2px 7px",
                    borderRadius: 999,
                    background: "var(--pos-bg)",
                    color: "var(--pos)",
                    border: "1px solid rgba(29,74,58,0.18)",
                  }}
                >
                  {t("auth.account.emailVerified")}
                </span>
              ) : (
                <span
                  className="inline-flex items-center"
                  style={{
                    gap: 4,
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    padding: "2px 7px",
                    borderRadius: 999,
                    background: "var(--warn-bg)",
                    color: "var(--warn)",
                    border: "1px solid rgba(138,90,0,0.18)",
                  }}
                >
                  {t("auth.account.emailUnverified")}
                </span>
              )}
            </span>
          }
        />
        <Row
          label={t("auth.account.role")}
          value={user.role === "pro" ? t("auth.account.rolePro") : t("auth.account.roleOrdinary")}
        />
        {user.role === "pro" && user.cea_number && (
          <Row
            label={t("auth.account.ceaRegNo", { defaultValue: "CEA reg #" })}
            value={
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13, color: "var(--ink)" }}>
                {user.cea_number}
              </span>
            }
          />
        )}

        {/* Upgrade CTA — surfaces only for ordinary users; lets buyer-
            account holders flip to property-agent role and gain access
            to /pro/dashboard. We don't link to a separate /upgrade page
            because the change is one PATCH, no payment / verification
            (CEA license check is out of scope per CLAUDE.md). */}
        {user.role !== "pro" && <UpgradeToProInline />}

        {!user.email_verified && (
          <div
            className="flex items-center justify-between"
            style={{
              marginTop: 14,
              border: "1px solid rgba(138,90,0,0.22)",
              background: "var(--warn-bg)",
              borderRadius: 10,
              padding: "11px 14px",
              gap: 12,
            }}
          >
            <div
              style={{
                fontSize: 12.5,
                color: "var(--warn)",
                lineHeight: 1.4,
              }}
            >
              {verifyErr ? (
                <span style={{ color: "var(--neg)" }}>{verifyErr}</span>
              ) : (
                <>
                  <strong style={{ fontWeight: 600 }}>{t("auth.verify.accountTitle")}</strong>{" "}
                  {t("auth.verify.accountBody")}
                </>
              )}
            </div>
            <button
              onClick={() => void handleResendVerify()}
              disabled={verifyBusy || verifySent}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--warn)",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: verifyBusy || verifySent ? "default" : "pointer",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                padding: "4px 0",
                opacity: verifyBusy || verifySent ? 0.7 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {verifySent
                ? t("auth.verify.sentShort")
                : verifyBusy
                ? t("auth.verify.resending")
                : t("auth.verify.resend")}
            </button>
          </div>
        )}
      </Section>

      {user.role === "pro" && (
        <>
          <Section title={t("auth.account.practice.title", { defaultValue: "Practice statistics" })}>
            <PracticeStatsSection ceaNumber={user.cea_number ?? ""} />
          </Section>
          <Section title={t("auth.account.proProfile.title", { defaultValue: "Pro agent profile" })}>
            <ProAgentProfileSection />
          </Section>
          <Section title={t("auth.account.invite.title", { defaultValue: "Invite buyers" })}>
            <InviteBuyersSection agentId={user.id} />
          </Section>
        </>
      )}

      <Section title={t("auth.account.passwordSection")}>
        <PasswordChangeForm />
      </Section>

      <Section title={t("auth.account.googleSection")}>
        <GoogleConnectSection
          hasGoogle={hasGoogle}
          accountEmail={user.email}
          googleEmail={user.google_email}
        />
      </Section>

      {/* My agents — buyer-side surface for the agent_user_links flow.
          Hidden for users with role=pro (agents see linked buyers in
          their own inbox, not here). */}
      {user.role !== "pro" && (
        <Section title={t("auth.account.myAgents", { defaultValue: "My agents" })}>
          <MyAgentsSection />
        </Section>
      )}

      <Section title={t("auth.account.danger")}>
        <div className="flex flex-wrap items-center justify-between" style={{ gap: 14 }}>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--ink-3)",
              flex: 1,
              minWidth: 200,
              lineHeight: 1.5,
            }}
          >
            {t("auth.account.signOutBody", {
              defaultValue:
                "Signs you out of this device only. Your inbox and saved threads stay safe in your account.",
            })}
          </div>
          <button
            onClick={() => void handleSignOut()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px 16px",
              borderRadius: 9,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              background: "transparent",
              color: "var(--neg)",
              border: "1px solid rgba(140,45,45,0.32)",
              letterSpacing: "-0.005em",
            }}
          >
            {t("auth.account.signOut")}
          </button>
        </div>
      </Section>

      <p
        style={{
          textAlign: "center",
          fontSize: 11,
          color: "var(--ink-4)",
          marginTop: 28,
          letterSpacing: "0.02em",
        }}
      >
        {t("auth.account.footnote", { defaultValue: "ProProp · Singapore · v2026.05" })}
      </p>
    </div>
  );
}
